import { pageRevision } from './revision';
import { underlayItems } from './underlay';
import { netLabelOf, pinLabels, potentialOf, structText, wireNode, type Analysis, type Potential } from './analyze';
import { instrumentSvg } from './fluid';
import { hasStandardSvg, standardOf, svgFor, type DrawingStandard } from './standards';
import { bbWorld, boxH, boxW, cableLen, decoPts, loc, pinsW, rotP, SHEET } from './geometry';
import { boxSvg, cableSvg, LIB } from './library';
import type { PartLookup } from './catalog';
import { layoutItems } from './layout';
import { reportPageItems } from './reportPages';
import type { Page, Placed, Project, Pt, Sym, Wire } from './types';
import { getLang, t as $t, tk } from './i18n';


/*
 * Output-independent display list. Screen (SVG), PDF and DXF are all drawn
 * from these items so the three outputs always match. Coordinates are world
 * mm with y pointing down; text y is the baseline.
 */

export type Colour = 'ink' | 'ink2' | 'frame' | 'accent' | 'paper' | 'pl' | 'pn' | 'ppe' | 'pdp' | 'pdn';
export type Font = 'ui' | 'mono' | 'cond';
export type Layer = 'frame' | 'wire' | 'symbol' | 'tag' | 'desc' | 'pin' | 'ref' | 'mirror' | 'wno' | 'label' | 'underlay';

/** Drawing layers in display order, with their names (translated at the display site). */
export const LAYERS: [Layer, string][] = [
  ['frame', tk('Çerçeve ve antet')],
  ['symbol', tk('Semboller')],
  ['wire', tk('Bağlantılar')],
  ['tag', tk('Cihaz etiketleri')],
  ['desc', tk('Açıklamalar')],
  ['pin', tk('Bağlantı noktası numaraları')],
  ['ref', tk('Çapraz referanslar')],
  ['mirror', tk('Kontak görüntüsü')],
  ['wno', tk('Tel numaraları')],
  ['label', tk('Diğer metinler')],
  ['underlay', tk('Altlık (içe aktarılan çizim)')],
];

/** Layers the project leaves out of printed output (PDF, DXF). */
export const unprintedLayers = (pr: Pick<Project, 'settings'>): Set<string> =>
  new Set(Object.entries(pr.settings?.layers ?? {}).filter(([, v]) => v?.noprint).map(([k]) => k));
/** Layers the project hides on screen. */
export const hiddenLayers = (pr: Pick<Project, 'settings'>): Set<string> =>
  new Set(Object.entries(pr.settings?.layers ?? {}).filter(([, v]) => v?.hide).map(([k]) => k));

export interface PolyItem {
  k: 'poly';
  pts: Pt[];
  closed?: boolean;
  stroke?: Colour;
  fill?: Colour;
  width: number;
  dash?: [number, number];
  layer: Layer;
}
export interface CircleItem {
  k: 'circle';
  c: Pt;
  r: number;
  stroke?: Colour;
  fill?: Colour;
  width: number;
  layer: Layer;
}
export interface TextItem {
  k: 'text';
  x: number;
  y: number;
  size: number;
  text: string;
  anchor: 'start' | 'middle' | 'end';
  font: Font;
  bold?: boolean;
  colour: Colour;
  /** rotation in degrees, clockwise (screen) */
  rot?: number;
  /** index of the project page this text refers to (cross-references) */
  link?: number;
  layer: Layer;
}
/** Raster picture (front-view images); `src` is the part number it belongs to. */
export interface ImageItem {
  k: 'image';
  x: number;
  y: number;
  w: number;
  h: number;
  src: string;
  layer: Layer;
}
export type Item = PolyItem | CircleItem | TextItem | ImageItem;

const PCOL: Record<string, Colour> = { L: 'pl', N: 'pn', PE: 'ppe', 'DC+': 'pdp', 'DC-': 'pdn' };
export const potentialColour = (p?: Potential): Colour => (p && PCOL[p.ptype]) || 'ink';

/* ---------------- SVG path flattening ---------------- */

/** Flattens an SVG path (M L H V A Z, absolute and relative) into polylines. */
export function flattenPath(d: string): { pts: Pt[]; closed: boolean }[] {
  const toks = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];
  const out: { pts: Pt[]; closed: boolean }[] = [];
  let cur: Pt = [0, 0], start: Pt = [0, 0], sub: Pt[] | null = null, cmd = '', i = 0;
  const num = () => parseFloat(toks[i++]);
  const begin = (p: Pt) => {
    sub = [p];
    out.push({ pts: sub, closed: false });
    start = p;
  };
  const lineTo = (p: Pt) => {
    if (!sub) begin(cur);
    sub!.push(p);
    cur = p;
  };
  while (i < toks.length) {
    if (/[a-zA-Z]/.test(toks[i])) cmd = toks[i++];
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    if (C === 'Z') {
      if (out.length) out[out.length - 1].closed = true;
      cur = start;
      sub = null;
      continue;
    }
    if (C === 'M') {
      const x = num(), y = num();
      cur = rel ? [cur[0] + x, cur[1] + y] : [x, y];
      begin(cur);
      cmd = rel ? 'l' : 'L';
    } else if (C === 'L') {
      const x = num(), y = num();
      lineTo(rel ? [cur[0] + x, cur[1] + y] : [x, y]);
    } else if (C === 'H') {
      const x = num();
      lineTo([rel ? cur[0] + x : x, cur[1]]);
    } else if (C === 'V') {
      const y = num();
      lineTo([cur[0], rel ? cur[1] + y : y]);
    } else if (C === 'A') {
      const rx = num(), ry = num(), phi = num(), large = num(), sweep = num();
      let x = num(), y = num();
      if (rel) {
        x += cur[0];
        y += cur[1];
      }
      for (const p of arcPoints(cur, [x, y], rx, ry, phi, !!large, !!sweep)) lineTo(p);
    } else {
      i++; // unsupported command: skip a token to avoid an endless loop
    }
  }
  return out;
}

/** Endpoint-parameterised SVG arc → points (W3C SVG implementation notes, F.6.5). */
function arcPoints(p1: Pt, p2: Pt, rx: number, ry: number, phiDeg: number, large: boolean, sweep: boolean): Pt[] {
  if (!rx || !ry) return [p2];
  const phi = (phiDeg * Math.PI) / 180, cs = Math.cos(phi), sn = Math.sin(phi);
  const dx = (p1[0] - p2[0]) / 2, dy = (p1[1] - p2[1]) / 2;
  const x1 = cs * dx + sn * dy, y1 = -sn * dx + cs * dy;
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const lam = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
  if (lam > 1) {
    rx *= Math.sqrt(lam);
    ry *= Math.sqrt(lam);
  }
  const num = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
  const den = rx * rx * y1 * y1 + ry * ry * x1 * x1;
  let co = Math.sqrt(Math.max(0, num / den));
  if (large === sweep) co = -co;
  const cx1 = (co * rx * y1) / ry, cy1 = (-co * ry * x1) / rx;
  const cx = cs * cx1 - sn * cy1 + (p1[0] + p2[0]) / 2, cy = sn * cx1 + cs * cy1 + (p1[1] + p2[1]) / 2;
  const ang = (ux: number, uy: number, vx: number, vy: number) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const t1 = ang(1, 0, (x1 - cx1) / rx, (y1 - cy1) / ry);
  let dt = ang((x1 - cx1) / rx, (y1 - cy1) / ry, (-x1 - cx1) / rx, (-y1 - cy1) / ry);
  if (!sweep && dt > 0) dt -= 2 * Math.PI;
  if (sweep && dt < 0) dt += 2 * Math.PI;
  const n = Math.max(4, Math.ceil(Math.abs(dt) / (Math.PI / 12)));
  const pts: Pt[] = [];
  for (let k = 1; k <= n; k++) {
    const t = t1 + (dt * k) / n;
    pts.push([cs * rx * Math.cos(t) - sn * ry * Math.sin(t) + cx, sn * rx * Math.cos(t) + cs * ry * Math.sin(t) + cy]);
  }
  pts[pts.length - 1] = p2;
  return pts;
}

/* ---------------- symbol graphics ---------------- */

type Shape =
  | { k: 'path'; d: string; dash: boolean; fill: boolean }
  | { k: 'rect'; x: number; y: number; w: number; h: number }
  | { k: 'circle'; x: number; y: number; r: number }
  | { k: 'text'; x: number; y: number; size: number; text: string };

const attr = (tag: string, name: string) => new RegExp(`\\s${name}="([^"]*)"`).exec(tag)?.[1] ?? '';
const shapeCache = new Map<string, Shape[]>();

/** Parses the library's own SVG snippets (path, rect, circle, text). */
export function symbolShapes(type: string, svgOverride?: string, std: DrawingStandard = 'iec'): Shape[] {
  const ck = hasStandardSvg(type, std) ? `${std}:${type}` : type;
  let s = svgOverride ? undefined : shapeCache.get(ck);
  if (s) return s;
  s = [];
  const re = /<(path|rect|circle|ellipse)\b[^>]*\/>|<text\b([^>]*)>([^<]*)<\/text>/g;
  const unescape = (t: string) => t.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
  const svg = svgOverride ?? svgFor(type, std);
  for (let m; (m = re.exec(svg)); ) {
    const tag = m[0];
    if (m[1] === 'path') s.push({ k: 'path', d: attr(tag, 'd'), dash: attr(tag, 'class') === 'dash', fill: attr(tag, 'class') === 'fill' });
    else if (m[1] === 'rect') s.push({ k: 'rect', x: +attr(tag, 'x'), y: +attr(tag, 'y'), w: +attr(tag, 'width'), h: +attr(tag, 'height') });
    else if (m[1] === 'circle') s.push({ k: 'circle', x: +attr(tag, 'cx'), y: +attr(tag, 'cy'), r: +attr(tag, 'r') });
    else if (m[1] === 'ellipse') {
      // an ellipse (summation transformer, float) as two elliptic arcs
      const cx = +attr(tag, 'cx'), cy = +attr(tag, 'cy'), rx = +attr(tag, 'rx'), ry = +attr(tag, 'ry');
      s.push({ k: 'path', d: `M${cx - rx} ${cy}A${rx} ${ry} 0 1 0 ${cx + rx} ${cy}A${rx} ${ry} 0 1 0 ${cx - rx} ${cy}`, dash: false, fill: false });
    } else s.push({ k: 'text', x: +attr(tag, 'x'), y: +attr(tag, 'y'), size: +attr(tag, 'font-size'), text: unescape(m[3]) });
  }
  if (!svgOverride) shapeCache.set(ck, s);
  return s;
}

/** SVG of one placed symbol (cables depend on their length). */
export const symbolSvg = (s: Sym, std: DrawingStandard = 'iec') => dynamicSvg(s, std) ?? svgFor(s.type, std);
/**
 * Mechanical interlock along its path (dashed): IEC 60617 S00154 marks it with a triangle, IEEE 315 (NFPA 79) with a
 * note (4.29: "mechanical interlocking should be indicated by notes").
 */
const mintSvg = (pts: Pt[], std: DrawingStandard) => {
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join('');
  const [a, b] = pts, m: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], flat = Math.abs(a[1] - b[1]) < 0.01;
  const mark = std === 'nfpa'
    ? `<text x="${m[0]}" y="${m[1] - 1}" font-size="1.8" text-anchor="middle">MECH. INTERLOCK</text>`
    : `<path d="${flat ? `M${m[0] - 2} ${m[1]}H${m[0] + 2}L${m[0]} ${m[1] + 2.4}Z` : `M${m[0]} ${m[1] - 2}V${m[1] + 2}L${m[0] + 2.4} ${m[1]}Z`}"/>`;
  return `<path d="${d}" class="dash"/>${mark}`;
};
/** Symbols whose outline depends on their properties. */
const dynamicSvg = (s: Sym, std: DrawingStandard = 'iec') => {
  const role = LIB[s.type].role;
  if (s.type === 'pid_inst') return instrumentSvg(s.props.isa || '', s.tag);
  if (s.type === 'mint') return mintSvg(decoPts(s), std);
  // a main switch that is a switch-disconnector, not a breaker (incomer of a distribution board)
  if (s.type === 'sl_cb' && s.props.swkind === 'lbs') return svgFor('sl_lbs', std);
  return role === 'cable' ? cableSvg(cableLen(s), s.props.shield === '1') : role === 'box' ? boxSvg(boxW(s), boxH(s)) : undefined;
};

const W_SYM = 0.35;

export function symbolItems(s: Sym, std: DrawingStandard = 'iec'): Item[] {
  const out: Item[] = [];
  const tf = (p: Pt): Pt => {
    const q = rotP(p[0], p[1], s.rot);
    return [s.x + q[0], s.y + q[1]];
  };
  for (const sh of symbolShapes(s.type, dynamicSvg(s, std), std)) {
    if (sh.k === 'path') {
      for (const sp of flattenPath(sh.d))
        out.push({ k: 'poly', pts: sp.pts.map(tf), closed: sp.closed, stroke: 'ink', fill: sh.fill ? 'ink' : undefined, width: W_SYM, dash: sh.dash ? [1.1, 0.9] : undefined, layer: 'symbol' });
    } else if (sh.k === 'rect') {
      const { x, y, w, h } = sh;
      out.push({ k: 'poly', pts: [[x, y], [x + w, y], [x + w, y + h], [x, y + h]].map((p) => tf(p as Pt)), closed: true, stroke: 'ink', width: W_SYM, layer: 'symbol' });
    } else if (sh.k === 'circle') {
      out.push({ k: 'circle', c: tf([sh.x, sh.y]), r: sh.r, stroke: 'ink', width: W_SYM, layer: 'symbol' });
    } else {
      const p = tf([sh.x, sh.y]);
      out.push({ k: 'text', x: p[0], y: p[1], size: sh.size, text: sh.text, anchor: 'middle', font: 'ui', bold: true, colour: 'ink', rot: s.rot * 90, layer: 'symbol' });
    }
  }
  return out;
}

/* ---------------- frame / title block ---------------- */

export interface FrameInfo {
  /** big page number/label in the title block */
  no: string;
  desc: string;
  total: string;
  /** index of the schematic page (for its revision) */
  pi?: number;
}

export function frameItems(pr: Project, f: FrameInfo): Item[] {
  const { X0, X1, Y0, Y1, HDR, COLW, COLS, TB_Y } = SHEET;
  const out: Item[] = [];
  const line = (pts: Pt[], width = 0.25, closed = false) => out.push({ k: 'poly', pts, closed, stroke: 'frame', width, layer: 'frame' });
  const t = (x: number, y: number, size: number, text: string, strong = false, anchor: TextItem['anchor'] = 'start') =>
    out.push({ k: 'text', x, y, size, text, anchor, font: strong ? 'cond' : 'ui', bold: strong, colour: strong ? 'ink' : 'ink2', layer: 'frame' });
  line([[X0, Y0], [X1, Y0], [X1, Y1], [X0, Y1]], 0.35, true);
  line([[X0, Y0 + HDR], [X1, Y0 + HDR]]);
  line([[X0, TB_Y], [X1, TB_Y]]);
  for (let c = 1; c < COLS; c++) {
    line([[X0 + c * COLW, Y0], [X0 + c * COLW, Y0 + HDR]]);
    line([[X0 + c * COLW, TB_Y - 2], [X0 + c * COLW, TB_Y]]);
  }
  // cells: company | project | page description | date, author, extra fields | sheet number
  const fr = pr.frame ?? {};
  const extra = (fr.fields ?? []).slice(0, 2);
  for (const x of [70, 170, 300, 360]) line([[x, TB_Y], [x, Y1]]);
  for (let c = 0; c < COLS; c++) t(X0 + c * COLW + COLW / 2, Y0 + 4.3, 3, String(c + 1), false, 'middle');
  // company: logo (fitted into the cell) or name, then the address line
  if (fr.logo) {
    out.push({ k: 'image', x: 13, y: 262, w: 54, h: fr.company || fr.address ? 16 : 24, src: fr.logo, layer: 'frame' });
    if (fr.company) t(13, 282.5, 2.6, fr.company, true);
  } else t(13, 270.5, fr.company ? 4 : 3.4, fr.company || 'EnerCAD', true);
  if (fr.address) t(13, 287, 2, fr.address);
  else if (!fr.logo && !fr.company) t(13, 276, 2.2, $t('Elektrik proje çizimi'));
  t(73, 264, 2.2, $t('PROJE'));
  t(73, 270.5, 4, pr.name, true);
  t(73, 276, 2.8, pr.customer);
  if (fr.drawingNo) t(73, 286.5, 2.4, $t('Çizim no: {0}', fr.drawingNo));
  t(173, 264, 2.2, $t("SAYFA AÇIKLAMASI"));
  t(173, 273, 5, f.desc || '—', true);
  t(173, 284, 2.6, $t('{0}   ·   {1} şema sayfası', structText({ fn: pr.fn || 'A1', site: pr.site, loc: pr.loc || 'PANO1', ud: pr.ud }), pr.pages.length));
  // a page shows the revision that last changed it; other sheets the latest one
  const rev = f.pi != null ? pageRevision(pr, f.pi) : pr.revision;
  const rows: [string, string][] = [
    [rev ? $t("TARİH · REVİZYON") : $t("TARİH"), rev ? $t("{0}  ·  Rev {1}", rev.date, rev.label) : pr.date],
    [$t("ÇİZEN"), pr.author],
    ...extra.map((e): [string, string] => [e.label.toLocaleUpperCase(getLang()), e.value]),
  ];
  const rh = (Y1 - TB_Y) / rows.length;
  rows.forEach(([label, value], i) => {
    const y0 = TB_Y + i * rh;
    if (i) line([[300, y0], [360, y0]]);
    t(303, y0 + (rows.length > 2 ? 2.8 : 5), rows.length > 2 ? 1.9 : 2.2, label);
    t(303, y0 + (rows.length > 2 ? 6.3 : 11), rows.length > 2 ? 2.6 : 3.2, value, true);
  });
  t(363, 264, 2.2, $t('SAYFA'));
  t(385, 282, f.no.length > 3 ? 7 : 11, f.no, true, 'middle');
  t(407, 286, 3, '/ ' + f.total, false, 'end');
  return out;
}

/* ---------------- labels, cross-references, contact mirror ---------------- */

const mirrorGlyph = (x: number, y: number, k?: string): Pt[][] =>
  k === 'nc'
    ? [[[x, y], [x, y + 0.9], [x - 1, y + 0.9]], [[x, y + 2.1], [x, y + 3]], [[x, y + 2.1], [x - 1.3, y + 0.4]]]
    : [[[x, y], [x, y + 0.9]], [[x, y + 2.1], [x, y + 3]], [[x, y + 2.1], [x - 1, y + 0.6]]];

type Line = { text: string; size: number; font: Font; bold?: boolean; colour: Colour; link?: number; layer: Layer };

/** Device tag, cross-references, pin numbers and contact mirror of one symbol. */
export function labelItems(A: Analysis, s: Sym, termSet: Set<string>): Item[] {
  const L = LIB[s.type], B = bbWorld(s), vert = s.rot % 2 === 0;
  const cx = (B[0] + B[2]) / 2, cy = (B[1] + B[3]) / 2;
  const out: Item[] = [];
  const t = (x: number, y: number, l: Line, anchor: TextItem['anchor']) => out.push({ k: 'text', x, y, anchor, ...l });
  if (L.role === 'text') {
    out.push({ k: 'text', x: s.x, y: s.y, size: Number(s.props.size) || 3.5, text: s.props.text || '', anchor: 'start', font: 'ui', colour: 'ink', layer: 'label', ...(s.rot % 4 ? { rot: (s.rot % 4) * 90 } : {}) });
    return out;
  }
  if (L.role === 'cable') return cableLabelItems(A, s);
  if (s.type === 'cdp') {
    // its values beside the rhombus: "2,5 mm² · BU · 101"
    const text = [s.props.cs ? `${s.props.cs.replace('.', ',')} mm²` : '', s.props.col ?? '', s.props.wno ?? ''].filter(Boolean).join(' · ');
    if (text) out.push({ k: 'text', x: s.x + 1.8, y: s.y - 1.2, size: 2.2, text, anchor: 'start', font: 'mono', colour: 'accent', layer: 'label' });
    return out;
  }
  if (L.role === 'box') {
    const head = structText(s) || '= +';
    out.push({ k: 'text', x: s.x + 1.5, y: s.y + 4, size: 3, text: head, anchor: 'start', font: 'mono', bold: true, colour: 'ink', layer: 'tag' });
    if (s.desc) out.push({ k: 'text', x: s.x + 1.5, y: s.y + 7.2, size: 2.2, text: s.desc, anchor: 'start', font: 'ui', colour: 'ink2', layer: 'desc' });
    return out;
  }
  const lines: Line[] = [];
  const ref = (it: Placed | undefined): Line => ({ text: it ? '/' + loc(it) : '/?', size: 2.3, font: 'mono', colour: 'accent', link: it?.pi, layer: 'ref' });
  if (L.role === 'pot' || L.role === 'brk') {
    lines.push({ text: s.props.name || '?', size: 3, font: 'mono', bold: true, colour: 'ink', layer: 'label' });
    if (L.role === 'brk') {
      const g = (A.brk.get(s.props.name || '') || []).filter((it) => it.s.id !== s.id);
      if (s.rot % 2 === 0 && g.length > 1) {
        // an upright connector (side by side with others): one cross-reference per line, each a link to its page
        for (const it of g.slice(0, g.length > 3 ? 2 : 3)) lines.push(ref(it));
        if (g.length > 3) lines.push({ ...ref(undefined), text: '…', link: undefined });
      } else {
        const r = ref(g[0]);
        if (g.length > 1) r.text = g.slice(0, 2).map((it) => '/' + loc(it)).join(' ') + (g.length > 2 ? ' …' : '');
        lines.push(r);
      }
    }
  } else {
    let show = !!s.tag;
    if (L.role === 'term' && termSet.has(`${s.tag}|${s.x - 8}|${s.y}`)) show = false;
    if (show) lines.push({ text: A.devLabel.get(s.id) ?? '-' + s.tag, size: 3, font: 'mono', bold: true, colour: 'ink', layer: 'tag' });
    if (L.role === 'aux') lines.push(ref(A.mains.get(A.devKey.get(s.id) ?? '')?.[0]));
    if (L.role === 'sl' && s.tag) {
      const m = A.mains.get(A.devKey.get(s.id) ?? '')?.[0];
      lines.push(ref(m));
      const d = s.desc || (s.type === 'sl_m' ? m?.s.desc : '');
      if (d) lines.push({ text: d, size: 2.2, font: 'ui', colour: 'ink2', layer: 'desc' });
    }
    if ((L.role === 'main' || L.role === 'plc') && s.desc) lines.push({ text: s.desc, size: 2.2, font: 'ui', colour: 'ink2', layer: 'desc' });
    // solenoid valve ↔ valve solenoid cross-references
    for (const k of A.valveSols.get(s.id) ?? []) {
      lines.push({ text: A.devName.get(k) ?? k.slice(k.lastIndexOf('-')), size: 2.6, font: 'mono', bold: true, colour: 'ink', layer: 'tag' });
      lines.push(ref(A.mains.get(k)?.[0]));
    }
    if (s.type === 'sol' && s.tag) {
      const v = A.solValves.get(A.devKey.get(s.id) ?? '');
      if (v?.length) lines.push(ref(v[0]));
    }
  }
  // approximate text width: monospaced tags, proportional descriptions
  const textW = (l: Line) => l.text.length * l.size * (l.font === 'mono' ? 0.62 : 0.45);
  // devices side by side closer than their tags are wide (the voltage fuses of a meter, 8 mm apart): the label
  // turns upright, reading from the bottom
  let upright = false;
  if (vert && L.role !== 'pot' && L.role !== 'brk') {
    // room on the left up to the next device on the page: a description that does not fit is wrapped onto more lines
    const pi = A.all.find((it) => it.s.id === s.id)?.pi;
    let left = -Infinity;
    for (const it of A.all) {
      if (it.pi !== pi || it.s.id === s.id || ['deco', 'text', 'box', 'cable'].includes(LIB[it.s.type]?.role)) continue;
      const o = bbWorld(it.s);
      if (o[1] > B[3] + 4 || o[3] < B[1] - 4) continue;
      if (o[2] <= B[0]) left = Math.max(left, o[2]);
    }
    const room = B[0] - 1.4 - left - 1;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (l.layer !== 'desc' || textW(l) <= room) continue;
      const rows: string[] = [];
      for (const word of l.text.split(' ')) {
        const last = rows.length ? `${rows[rows.length - 1]} ${word}` : word;
        if (rows.length && textW({ ...l, text: last }) <= room) rows[rows.length - 1] = last;
        else rows.push(word);
      }
      lines.splice(i, 1, ...rows.map((text) => ({ ...l, text })));
      i += rows.length - 1;
    }
    const fixed = lines.filter((l) => l.layer !== 'desc');
    // upright between the two bodies, half a millimetre clear of each
    const need = lines.reduce((a, l) => a + l.size + 0.6, 0) - 0.6;
    upright = fixed.some((l) => textW(l) > room) && B[0] - left >= need + 1 && Math.max(...lines.map(textW)) <= B[3] - B[1] + 6;
  }
  const H = lines.reduce((a, l) => a + l.size + 0.8, 0) - 0.8;
  if (upright) {
    let x = B[0] - 0.5;
    for (const l of lines) {
      x -= l.size * 0.2;
      out.push({ k: 'text', x, y: cy, anchor: 'middle', ...l, rot: 270 });
      x -= l.size * 0.8 + 0.6;
    }
  } else if (vert || L.role === 'pot' || L.role === 'brk') {
    const x1 = B[0] - 1.4;
    // a page connector's label on its left, up to the frame or the nearest wire or device there (connectors side by
    // side 16 mm apart): a line that does not fit (its cross-references) goes right of it, under its wire, where no
    // other wire runs; else only as many cross-references as fit stay on the left. Nothing runs out of the frame or
    // over another wire.
    if (L.role === 'pot' || L.role === 'brk') {
      const pi = A.all.find((it) => it.s.id === s.id)?.pi;
      const segs = (pi != null ? A.pr.pages[pi].wires : []).flatMap((w) => w.pts.slice(1).map((b, i) => [w.pts[i], b]));
      const clear = (x0: number, y0: number, x2: number, y2: number) =>
        !segs.some(([a, b]) => Math.max(a[0], b[0]) > x0 && Math.min(a[0], b[0]) < x2 && Math.max(a[1], b[1]) > y0 && Math.min(a[1], b[1]) < y2) &&
        !A.all.some((it) => {
          if (it.pi !== pi || it.s.id === s.id || ['deco', 'text', 'box', 'cable'].includes(LIB[it.s.type]?.role)) return false;
          const o = bbWorld(it.s);
          return o[0] < x2 && o[2] > x0 && o[1] < y2 && o[3] > y0;
        });
      const H0 = lines.reduce((a, l) => a + l.size + 0.8, 0) - 0.8, y0 = cy - H0 / 2 - 0.3, y2 = cy + H0 / 2 + 0.3;
      let left = SHEET.X0 + 0.5;
      for (const [a, b] of segs)
        if (Math.max(a[0], b[0]) <= x1 && Math.max(a[1], b[1]) > y0 && Math.min(a[1], b[1]) < y2) left = Math.max(left, Math.max(a[0], b[0]) + 0.6);
      for (const it of A.all) {
        if (it.pi !== pi || it.s.id === s.id || ['deco', 'text', 'box', 'cable'].includes(LIB[it.s.type]?.role)) continue;
        const o = bbWorld(it.s);
        if (o[2] <= x1 && o[3] > y0 && o[1] < y2) left = Math.max(left, o[2] + 0.6);
      }
      const room = x1 - left;
      if (s.rot % 2 === 0 && !lines.every((l) => textW(l) <= room)) {
        // an upright connector too close to the next one for even its name: the label turns upright beside its
        // wire, past the arrow (below it when the arrow points up), name first; cross-references only as far as no
        // device on the wire is in the way
        const up = s.rot === 2, xb = s.x - 1.2;
        const free = (a: number, b: number) => clear(xb - 2.4, a, xb + 0.7, b);
        const [name, ...rest] = lines;
        const refs = rest.filter((l) => l.text !== '…'), more = rest.length > refs.length;
        const items = (n: number) => [name, ...refs.slice(0, n), ...(n < refs.length || more ? [{ ...refs[0], text: '…', link: undefined }] : [])];
        const length = (ls: Line[]) => ls.reduce((a, l) => a + textW(l) + 1.2, -1.2);
        // along the wire (the label grows away from the arrow), else beyond the arrow's tip, as many
        // cross-references as fit; last resort: the name alone along the wire
        const sides = [up, !up].map((down) => ({ down, from: down ? B[3] + 1 : B[1] - 1 }));
        const fits = (side: { down: boolean; from: number }, ls: Line[]) => {
          const len = length(ls);
          const [a, b] = side.down ? [side.from, side.from + len] : [side.from - len, side.from];
          return a > SHEET.Y0 + SHEET.HDR && b < SHEET.TB_Y && free(a, b);
        };
        let pick: { side: (typeof sides)[number]; ls: Line[] } | undefined;
        search: for (const side of sides)
          for (let n = refs.length; n >= 0; n--)
            if (fits(side, items(n))) {
              pick = { side, ls: items(n) };
              break search;
            }
        // no room for it upright either: level, centred beyond the arrow's tip
        const tip = sides[1];
        const level = (ls: Line[]) => {
          const h = ls.reduce((a, l) => a + l.size + 0.8, -0.8), w = Math.max(...ls.map(textW));
          const [a, b] = tip.down ? [tip.from, tip.from + h] : [tip.from - h, tip.from];
          return a > SHEET.Y0 + SHEET.HDR && b < SHEET.TB_Y && clear(s.x - w / 2, a, s.x + w / 2, b) ? a : null;
        };
        let flat: { top: number; ls: Line[] } | undefined;
        // (the cross-references on one line here: "/11.7 /13.7 …")
        const row = (n: number): Line[] =>
          n ? [name, { ...refs[0], text: refs.slice(0, n).map((l) => l.text).join(' ') + (n < refs.length || more ? ' …' : ''), link: refs[0].link }] : [name];
        if (!pick)
          for (let n = refs.length; n >= 0 && !flat; n--) {
            const top = level(row(n));
            if (top != null) flat = { top, ls: row(n) };
          }
        if (flat) {
          let y = flat.top;
          for (const l of flat.ls) {
            t(s.x, y + l.size * 0.8, l, 'middle');
            y += l.size + 0.8;
          }
        } else {
          // last resort: the name alone upright along the wire
          const p = pick ?? { side: sides[0], ls: [name] };
          let c = p.side.from;
          for (const l of p.ls) {
            out.push({ k: 'text', x: xb, y: c, anchor: p.side.down ? 'end' : 'start', ...l, rot: 270 });
            c += (p.side.down ? 1 : -1) * (textW(l) + 1.2);
          }
        }
        lines.length = 0;
      }
      // every line keeps its place in the stack (the name above the wire, the cross-references under it), on
      // whichever side it goes; a line drawn on the right leaves an empty line on the left
      const slot: number[] = [];
      lines.reduce((yy, l) => (slot.push(yy), yy + l.size + 0.8), cy - H0 / 2);
      const right = (i: number) => {
        const l = lines[i];
        return B[2] + 1 + textW(l) <= SHEET.X1 && clear(B[2] + 1, slot[i], B[2] + 1 + textW(l), slot[i] + l.size);
      };
      const toRight = (i: number) => {
        t(B[2] + 1, slot[i] + lines[i].size * 0.8, lines[i], 'start');
        lines[i] = { ...lines[i], text: '' };
      };
      // not even the name fits on the left: the whole label right of the arrow, where there is room for it
      if (lines.length && textW(lines[0]) > room && lines.every((_, i) => right(i))) lines.forEach((_, i) => toRight(i));
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        if (textW(l) <= room) continue;
        if (right(i)) {
          toRight(i);
          continue;
        }
        const refs = l.text.replace(/ …$/, '').split(' ');
        const cut = (n: number, more: boolean) => refs.slice(0, n).join(' ') + (more ? ' …' : '');
        let n = refs.length;
        while (n > 1 && textW({ ...l, text: cut(n, n < refs.length || l.text.endsWith('…')) }) > room) n--;
        const more = n < refs.length || l.text.endsWith('…');
        lines[i] = { ...l, text: textW({ ...l, text: cut(n, more) }) <= room ? cut(n, more) : cut(n, false) };
      }
    }
    const H = lines.reduce((a, l) => a + l.size + 0.8, 0) - 0.8;
    let y = cy - H / 2;
    // a drawing-only symbol (the mechanical interlock between two contactors) running through the label: the
    // label moves up, clear of it
    const pi = A.all.find((it) => it.s.id === s.id)?.pi;
    const w = Math.max(...lines.map(textW));
    for (const it of A.all) {
      if (it.pi !== pi || LIB[it.s.type]?.role !== 'deco') continue;
      // segment by segment (an interlock may turn corners round other devices)
      const pts = decoPts(it.s).map((q) => {
        const r = rotP(q[0], q[1], it.s.rot);
        return [it.s.x + r[0], it.s.y + r[1]] as Pt;
      });
      for (let k = 1; k < pts.length; k++) {
        const [a, b] = [pts[k - 1], pts[k]];
        const d = [Math.min(a[0], b[0]) - 1, Math.min(a[1], b[1]) - 1, Math.max(a[0], b[0]) + 1, Math.max(a[1], b[1]) + 2.6];
        if (d[0] < x1 && d[2] > x1 - w && d[1] < y + H && d[3] > y) y = Math.min(y, d[1] - 1 - H);
      }
    }
    for (const l of lines) {
      y += l.size * 0.8;
      if (l.text) t(B[0] - 1.4, y, l, 'end');
      y += l.size * 0.2 + 0.8;
    }
  } else {
    // above the symbol, or below it where leads leave its top (the secondaries of a current transformer)
    const top = pinsW(s).some((p) => p[1] <= B[1] + 0.5) && !pinsW(s).some((p) => p[1] >= B[3] - 0.5);
    let y = top ? B[3] + 1.4 : B[1] - 1.4 - H;
    for (const l of lines) {
      y += l.size * 0.8;
      t(cx, y, l, 'middle');
      y += l.size * 0.2 + 0.8;
    }
  }
  const small = (text: string, colour: Colour = 'ink2', bold = false, layer: Layer = 'pin'): Line => ({ text, size: 2, font: 'mono', colour, bold, layer });
  if (L.role === 'term') {
    const l = { ...small(s.props.pinNo || '?', 'ink'), size: 2.4 };
    if (vert) t(s.x + 2.3, s.y + 5.1, l, 'start');
    else t(s.x, B[1] - 1, l, 'middle');
    return out;
  }
  if (L.role === 'plc' && L.box) {
    const q = rotP(L.box[0], L.box[1], s.rot);
    t(s.x + q[0], s.y + q[1] + 0.85, { ...small(s.props.addr || '?', 'ink', true), size: 2.4 }, 'middle');
    return out;
  }
  if (L.role === 'main' || L.role === 'aux') {
    const labs = L.role === 'aux' ? A.auxLabels.get(s.id) || [] : pinLabels(s);
    pinsW(s).forEach((p, i) => {
      const lab = labs[i];
      if (!lab) return;
      if (vert) {
        const inward = cy > p[1];
        t(p[0] + 0.9, inward ? p[1] + 2.6 : p[1] - 1, small(lab), 'start');
      } else {
        const inward = cx > p[0];
        t(inward ? p[0] + 1.2 : p[0] - 1.2, p[1] - 1, small(lab), inward ? 'start' : 'end');
      }
    });
  }
  const key = A.devKey.get(s.id) ?? '';
  if (L.mirror && s.tag && A.mains.get(key)?.[0].s.id === s.id) {
    let y = B[1] + 1.5;
    const x = B[2] + 3;
    for (const it of A.auxs.get(key) || []) {
      const lb = A.auxLabels.get(it.s.id) || [], k = LIB[it.s.type].kind;
      const rows: [string, string | undefined][] =
        it.s.type === 'k3'
          ? [[`${lb[0]}-${lb[3]}`, 'no'], [`${lb[1]}-${lb[4]}`, 'no'], [`${lb[2]}-${lb[5]}`, 'no']]
          : it.s.type === 'k3y'
          ? [['1-2', 'no'], ['3-4', 'no'], ['5-6', 'no']]
          : it.s.type === 'co'
          ? [[`${lb[0]}-${lb[2]}`, 'no'], [`${lb[0]}-${lb[1]}`, 'nc']]
          : !k
          ? []
          : [[`${lb[0]}-${lb[1]}`, k]];
      for (const [pn, kk] of rows) {
        for (const pts of mirrorGlyph(x, y, kk)) out.push({ k: 'poly', pts, stroke: 'ink2', width: 0.25, layer: 'mirror' });
        t(x + 1.4, y + 2.4, { ...small(pn, 'ink2', false, 'mirror'), size: 2.2 }, 'start');
        t(x + 10, y + 2.4, { ...ref(it), size: 2.2, layer: 'mirror' }, 'start');
        y += 3.4;
      }
    }
  }
  return out;
}

export const terminalSet = (syms: Sym[]) => new Set(syms.filter((s) => s.type === 'term').map((s) => `${s.tag}|${s.x}|${s.y}`));

/* ---------------- whole page ---------------- */

export interface PageDrawOptions {
  netNumbers?: boolean;
  /** parts lookup for device sizes on layout pages */
  parts?: PartLookup;
  /** front views of parts for layout pages */
  fronts?: import('./front').FrontLookup;
}

/**
 * Size of a copper busbar written above its longest horizontal run (runs of at least 80 mm), 30 mm from its start
 * or, given the page, at the nearest place along the run where no device or crossing wire is under the text.
 */
export function busLabel(w: Wire, page?: Page): TextItem | null {
  if (!w.bus) return null;
  let best: [Pt, Pt] | null = null;
  for (let i = 0; i < w.pts.length - 1; i++) {
    const a = w.pts[i], b = w.pts[i + 1];
    if (a[1] === b[1] && Math.abs(b[0] - a[0]) >= 80 && (!best || Math.abs(b[0] - a[0]) > Math.abs(best[1][0] - best[0][0]))) best = [a, b];
  }
  if (!best) return null;
  const text = `Cu ${w.bus}`, y = best[0][1] - 1.2, x0 = Math.min(best[0][0], best[1][0]), x1 = Math.max(best[0][0], best[1][0]);
  const tw = text.length * 1.8 * 0.62;
  let x = x0 + 30;
  if (page) {
    const boxes = page.syms.filter((s) => !['deco', 'text'].includes(LIB[s.type]?.role ?? '')).map((s) => bbWorld(s));
    const segs = page.wires.filter((o) => o.id !== w.id).flatMap((o) => o.pts.slice(1).map((p, i) => [o.pts[i], p] as [Pt, Pt]));
    const free = (tx: number) => {
      const r = [tx - 0.8, y - 2, tx + tw + 0.8, y + 0.8];
      if (boxes.some((b) => b[0] < r[2] && b[2] > r[0] && b[1] < r[3] && b[3] > r[1])) return false;
      return !segs.some(([a, b]) => Math.min(a[0], b[0]) <= r[2] && Math.max(a[0], b[0]) >= r[0] && Math.min(a[1], b[1]) <= r[3] && Math.max(a[1], b[1]) >= r[1]);
    };
    const tries: number[] = [];
    for (let tx = x0 + 4; tx + tw <= x1 - 4; tx += 2) tries.push(tx);
    tries.sort((p, q) => Math.abs(p - x) - Math.abs(q - x));
    x = tries.find(free) ?? x;
  }
  return { k: 'text', x, y, size: 1.8, text, anchor: 'start', font: 'mono', colour: 'ink2', layer: 'label' };
}

/** Everything on one schematic page, in drawing order. */
function allPageItems(pr: Project, A: Analysis, pi: number, o: PageDrawOptions = {}): Item[] {
  const page = pr.pages[pi];
  const out: Item[] = frameItems(pr, { no: String(pi + 1), desc: page.desc, total: String(pr.pages.length), pi });
  if (page.kind === 'layout') return [...out, ...layoutItems(pr, A, pi, o.parts, o.fronts)];
  if (page.kind === 'report') return [...out, ...reportPageItems(pr, A, page, o.parts)];
  if (page.underlay) out.push(...underlayItems(page.underlay));
  for (const w of page.wires) {
    out.push({ k: 'poly', pts: w.pts, stroke: potentialColour(potentialOf(A, wireNode(w.id))), width: w.bus ? 1.1 : 0.35, layer: 'wire' });
    const bl = busLabel(w, page);
    if (bl) out.push(bl);
    if (o.netNumbers) out.push(...netNumberItems(A, w.id, w.pts));
  }
  for (const j of A.junctions[pi] || []) {
    const c = potentialColour(potentialOf(A, j.node));
    out.push({ k: 'circle', c: [j.x, j.y], r: 0.75, fill: c, width: 0, layer: 'wire' });
  }
  const ts = terminalSet(page.syms), std = standardOf(pr);
  for (const s of page.syms) {
    out.push(...symbolItems(s, std));
    out.push(...labelItems(A, s, ts));
  }
  return out;
}

/** Everything on one schematic page that goes into printed output (layers switched off in the project are left out). */
export function pageItems(pr: Project, A: Analysis, pi: number, o: PageDrawOptions = {}): Item[] {
  const off = unprintedLayers(pr);
  const items = allPageItems(pr, A, pi, o);
  return off.size ? items.filter((it) => !off.has(it.layer)) : items;
}

export function netNumberItems(A: Analysis, wireId: string, pts: Pt[]): TextItem[] {
  let best = 0, seg: [Pt, Pt] | null = null;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1], l = Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
    if (l > best) {
      best = l;
      seg = [a, b];
    }
  }
  if (!seg) return [];
  const n = netLabelOf(A, wireNode(wireId));
  const x = (seg[0][0] + seg[1][0]) / 2, y = (seg[0][1] + seg[1][1]) / 2, v = seg[0][0] === seg[1][0];
  return [{ k: 'text', x: v ? x + 0.8 : x, y: v ? y + 0.7 : y - 0.8, size: 2, text: n, anchor: v ? 'start' : 'middle', font: 'mono', bold: true, colour: 'accent', layer: 'wno' }];
}

/** Cable tag, type and the core designation at each crossing. */
function cableLabelItems(A: Analysis, s: Sym): Item[] {
  const out: Item[] = [];
  const info = A.cables.get(s.id);
  const horiz = s.rot % 2 === 0, B = bbWorld(s);
  const n = info?.cores.length ?? 0;
  const type = [s.props.cableType, n ? `${n}×${s.props.cs || '?'}` : '', s.props.shield === '1' ? '+SH' : ''].filter(Boolean).join(' ');
  const head: TextItem = { k: 'text', x: B[0] - 1.5, y: s.y - 0.4, size: 3, text: A.devLabel.get(s.id) ?? '-?', anchor: 'end', font: 'mono', bold: true, colour: 'ink', layer: 'tag' };
  const sub: TextItem = { ...head, y: s.y + 2.6, size: 2.2, text: type, bold: false, font: 'ui', colour: 'ink2', layer: 'desc' };
  if (!horiz) {
    Object.assign(head, { x: s.x, y: B[1] - 4.2, anchor: 'middle' });
    Object.assign(sub, { x: s.x, y: B[1] - 1.4, anchor: 'middle' });
  }
  out.push(head);
  if (type) out.push(sub);
  for (const c of info?.cross || [])
    out.push({
      k: 'text', x: horiz ? c.pt[0] + 0.7 : c.pt[0] - 0.7, y: horiz ? c.pt[1] - 1.1 : c.pt[1] - 0.7, size: 2, text: c.core || '!',
      anchor: horiz ? 'start' : 'end', font: 'mono', bold: true, colour: c.core ? 'accent' : 'ink', layer: 'label',
    });
  return out;
}
